import { useEffect, useState } from 'react'

import {
    COLLABORATION_TYPES,
    isCollaborationType,
} from '../../../../../../common/collaboration-types.js'
import { t } from '../../../../../../common/i18n.js'
import {
    isLeavingReason,
    isTurnoverImpact,
    LEAVING_REASONS,
    TURNOVER_IMPACTS,
} from '../../../../../../common/leaving-types.js'
import { Language } from '../../../../../../common/types/i18n.js'
import { formatDisplayDate, formatFte } from '../../../../../../common/utils/format-utils.js'
import { Button } from '../../../../../components/button/button.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { Briefcase, Plus, X } from '../../../../../components/icon/solar-icons.js'
import {
    PortalDataTable,
    type PortalDataTableColumn,
} from '../../../../../components/portal-data-table/portal-data-table.js'
import {
    type OffboardFormData,
    PortalOffboardModal,
} from '../../../../../components/portal-offboard-modal/portal-offboard-modal.js'
import { FieldRow, Section, SectionHeader } from '../../../../../components/section/section.js'
import { ToggleGroup } from '../../../../../components/toggle-group/toggle-group.js'
import {
    WorkingPatternDayGrid,
    WorkingPatternFtePreview,
} from '../../../../../components/working-pattern-day-grid/working-pattern-day-grid.js'
import { showToast } from '../../../../../toast.js'
import { getStatusLabel } from '../../../utils/format.js'
import {
    assignWorkingPatternAllocation,
    deleteWorkingPatternAllocation,
    fetchSettingsWorkingPatterns,
    type SettingsWorkingPattern,
} from '../../../utils/portal-api.js'
import type {
    EmployeeProfileData,
    RequestConfirmFn,
    WorkingPatternHistoryRecord,
} from '../employee-profile-types.js'
import '../employee-profile-view.css'
import {
    type AssignFormState,
    buildAssignRequest,
    deriveWeeklyHours,
    isAssignReady,
} from './working-pattern-assign.js'

/** Parse a stored working_pattern into a numeric day grid for a read-only
 *  preview. Booleans (flex / Humaans-imported rows) map to the standard day. */
const parsePreviewPattern = (
    pattern: SettingsWorkingPattern | undefined,
): { mode: 'fixed' | 'flex'; days: number[] } | null => {
    if (!pattern) return null
    let raw: unknown
    try {
        raw = JSON.parse(pattern.working_pattern)
    } catch {
        return null
    }
    if (!Array.isArray(raw) || raw.length === 0) return null
    const arr = raw as (number | boolean)[]
    const std = Number(pattern.standard_day_hours ?? 8)
    const days = Array.from({ length: 7 }, (_, i) => {
        const slot = arr[i]
        if (typeof slot === 'number') return slot
        if (typeof slot === 'boolean') return slot ? std : 0
        return 0
    })
    return { mode: arr.some((v) => typeof v === 'boolean') ? 'flex' : 'fixed', days }
}

interface EmploymentSectionProps {
    employee: EmployeeProfileData
    workingPatterns: WorkingPatternHistoryRecord[]
    isAdmin: boolean
    // Work-data surface (koostöövorm, katseaeg, tööaeg) — self/manager/admin.
    canViewWorkData: boolean
    canEditWorkData: boolean
    // Compensation-tier: gates the contract signing date row.
    showCompensation: boolean
    lang: Language
    // In-place corrections of the employment record's anchor dates, probation
    // end and collaboration form (the section-header "Muuda").
    onUpdateEmploymentDates?: (data: {
        hire_date?: string | null
        contract_start?: string | null
        probation_end_date?: string | null
        collaboration_type?: string | null
    }) => Promise<boolean>
    // Offboarding status flip (offboard / reactivate). Present only when the
    // viewer holds `offboard_users`; gated server-side by the same capability.
    canOffboardUsers?: boolean
    onChangeStatus?: (status: 'active' | 'inactive' | 'on_leave') => Promise<boolean>
    // Record the leaving details ("Lõpeta töösuhe" form). Same viewer gate as
    // onChangeStatus; the status flips only once the end date has passed.
    onOffboard?: (data: OffboardFormData) => Promise<boolean>
    // Edits the recorded "Lahkumisandmed" fields (admin).
    onUpdatePersonal?: (data: Record<string, string>) => Promise<boolean>
    onWorkingPatternsRefresh?: () => Promise<void>
    requestConfirm: RequestConfirmFn
}

/**
 * "Töösuhe ja -koormus" — contract/employment info (TASK-438): status, hire
 * and contract dates, collaboration form, probation end, plus the working-time
 * & workload (FTE) block. Header carries "Muuda" and the dynamic offboarding
 * module ("Lõpeta töösuhe" while active); once the leaving form is confirmed a
 * "Lahkumisandmed" sub-block appears with its own edit affordance.
 */
export const EmploymentSection = ({
    employee,
    workingPatterns,
    isAdmin,
    canViewWorkData,
    canEditWorkData,
    showCompensation,
    lang,
    onUpdateEmploymentDates,
    canOffboardUsers,
    onChangeStatus,
    onOffboard,
    onUpdatePersonal,
    onWorkingPatternsRefresh,
    requestConfirm,
}: EmploymentSectionProps) => {
    const ph = t.portal
    const wpStrings = t.portal.settings.workingPatterns
    const emp = employee.employment

    // Section edit mode: hire date, collaboration form, probation end,
    // contract signing date. In-place corrections of the active record.
    const [isEditing, setIsEditing] = useState(false)
    const [editHireDate, setEditHireDate] = useState('')
    const [editContractStart, setEditContractStart] = useState('')
    const [editProbationEnd, setEditProbationEnd] = useState('')
    const [editCollaborationType, setEditCollaborationType] = useState('')
    const [saving, setSaving] = useState(false)

    // Matches the route/server gate: the employment-dates PATCH is wired (and
    // authorised) for employment editors, not compensation editors.
    const canEdit = (employee.viewer?.canEditEmployment ?? false) && !!onUpdateEmploymentDates

    const openEdit = () => {
        setEditHireDate(emp?.hire_date ?? '')
        setEditContractStart(emp?.contract_start ?? '')
        setEditProbationEnd(emp?.probation_end_date ?? '')
        setEditCollaborationType(emp?.collaboration_type ?? '')
        setIsEditing(true)
    }

    const submitEdit = async () => {
        setSaving(true)
        try {
            const dateChanges: {
                hire_date?: string | null
                contract_start?: string | null
                probation_end_date?: string | null
                collaboration_type?: string | null
            } = {}
            if (editHireDate !== (emp?.hire_date ?? '')) {
                dateChanges.hire_date = editHireDate || null
            }
            if (editContractStart !== (emp?.contract_start ?? '')) {
                dateChanges.contract_start = editContractStart || null
            }
            if (editProbationEnd !== (emp?.probation_end_date ?? '')) {
                dateChanges.probation_end_date = editProbationEnd || null
            }
            if (editCollaborationType !== (emp?.collaboration_type ?? '')) {
                dateChanges.collaboration_type = editCollaborationType || null
            }
            if (Object.keys(dateChanges).length > 0 && onUpdateEmploymentDates) {
                const ok = await onUpdateEmploymentDates(dateChanges)
                if (!ok) return
            }
            setIsEditing(false)
        } finally {
            setSaving(false)
        }
    }

    // Offboarding admin edit mode state (the "Lahkumisandmed" sub-block)
    const [isEditingOffboarding, setIsEditingOffboarding] = useState(false)
    const [editOffboardingData, setEditOffboardingData] = useState<Record<string, string>>({})
    const [offboardingSaving, setOffboardingSaving] = useState(false)
    const [statusSaving, setStatusSaving] = useState(false)

    // "Lõpeta töösuhe" opens the leaving-details form (TASK-403); the status
    // itself flips only once the recorded end date has passed.
    const [isOffboardModalOpen, setIsOffboardModalOpen] = useState(false)

    // Reactivate: flips the lifecycle status back via its own endpoint (and
    // clears the recorded leaving details server-side). Confirmed first.
    const reactivate = () => {
        if (!onChangeStatus) return
        requestConfirm(
            ph.profile.reactivateConfirm(lang),
            () => {
                setStatusSaving(true)
                void onChangeStatus('active').then(() => setStatusSaving(false))
            },
            { confirmLabel: ph.profile.reactivateAction(lang), variant: 'primary' },
        )
    }
    // isAdmin stays part of the gate (as in the pre-restructure layout): the
    // recorded "Lahkumisandmed" block below is admin-only, so a non-admin
    // offboarder could otherwise record leaving data they can never see or fix.
    const showOffboardAction = isAdmin && canOffboardUsers && !!onOffboard
    const showReactivateAction = isAdmin && canOffboardUsers && !!onChangeStatus

    // The "Lahkumisandmed" rows appear only once the form has been confirmed
    // (or legacy leaving fields already hold data).
    const hasLeavingData = !!(
        employee.employment_end_date ||
        employee.leaving_file ||
        employee.turnover_impact ||
        employee.leaving_reason
    )

    // Working pattern assignment slide-over state. Humaans-style: pick an
    // admin-defined pattern, or define a per-person custom one with an explicit
    // day grid — fixed (hours/day) or flex (working days, no fixed hours).
    const [showAssignPattern, setShowAssignPattern] = useState(false)
    const [availablePatterns, setAvailablePatterns] = useState<SettingsWorkingPattern[]>([])
    const [assignPatternMode, setAssignPatternMode] = useState<'existing' | 'custom'>('existing')
    const [assignCustomKind, setAssignCustomKind] = useState<'fixed' | 'flex'>('fixed')
    const [assignName, setAssignName] = useState('')
    const [assignDays, setAssignDays] = useState<number[]>([8, 8, 8, 8, 8, 0, 0])
    const [assignFullTimeHours, setAssignFullTimeHours] = useState('40')
    const [assignStandardDay, setAssignStandardDay] = useState('8')
    const [assignWeeklyHours, setAssignWeeklyHours] = useState('40')
    const [assignPatternId, setAssignPatternId] = useState<number>(0)
    const [assignEffectiveFrom, setAssignEffectiveFrom] = useState(
        new Date().toISOString().slice(0, 10),
    )
    const [assignSubmitting, setAssignSubmitting] = useState(false)

    useEffect(() => {
        if (!showAssignPattern) return
        void (async () => {
            const r = await fetchSettingsWorkingPatterns()
            if (r.ok) {
                setAvailablePatterns(r.data)
                if (r.data.length > 0 && assignPatternId === 0) {
                    setAssignPatternId(r.data[0].id)
                }
            }
        })()
    }, [showAssignPattern])

    const wpDayLabels = {
        mon: wpStrings.day.mon(lang),
        tue: wpStrings.day.tue(lang),
        wed: wpStrings.day.wed(lang),
        thu: wpStrings.day.thu(lang),
        fri: wpStrings.day.fri(lang),
        sat: wpStrings.day.sat(lang),
        sun: wpStrings.day.sun(lang),
    }

    const assignFullTime = Number(assignFullTimeHours) || 40
    const assignStandardDayHours = Number(assignStandardDay) || 8
    const assignFormState: AssignFormState = {
        mode: assignPatternMode,
        customKind: assignCustomKind,
        patternId: assignPatternId,
        name: assignName,
        days: assignDays,
        weeklyHours: Number(assignWeeklyHours) || 0,
        fullTimeHours: assignFullTime,
        standardDayHours: assignStandardDayHours,
        effectiveFrom: assignEffectiveFrom,
    }
    const assignWorkingHours = deriveWeeklyHours(assignFormState)
    const assignDaysPerWeek = assignDays.filter((d) => d > 0).length
    const assignReady = isAssignReady(assignFormState)

    // Read-only day grid shown for the picked existing pattern (Humaans shows
    // the schedule you're assigning). Boolean (imported/flex) rows render as
    // standard-day hours so the grid still reads as a week.
    const existingPreview =
        assignPatternMode === 'existing'
            ? parsePreviewPattern(availablePatterns.find((p) => p.id === assignPatternId))
            : null

    const handleAssignPattern = async () => {
        if (!assignReady) return
        setAssignSubmitting(true)
        try {
            const r = await assignWorkingPatternAllocation(
                employee.id,
                buildAssignRequest(assignFormState),
            )
            if (!r.ok) {
                showToast(r.error, 'error')
                return
            }
            showToast(wpStrings.allocationCreated(lang), 'success')
            setShowAssignPattern(false)
            setAssignPatternMode('existing')
            setAssignCustomKind('fixed')
            setAssignName('')
            setAssignDays([8, 8, 8, 8, 8, 0, 0])
            setAssignFullTimeHours('40')
            setAssignStandardDay('8')
            setAssignWeeklyHours('40')
            setAssignPatternId(0)
            setAssignEffectiveFrom(new Date().toISOString().slice(0, 10))
            if (onWorkingPatternsRefresh) await onWorkingPatternsRefresh()
        } finally {
            setAssignSubmitting(false)
        }
    }

    // Only future allocations are deletable (the server reopens the previous
    // SCD2 row); past/current rows are history and stay immutable.
    const handleDeleteAllocation = (allocationId: number) => {
        requestConfirm(
            ph.compensation.confirmDelete(lang),
            () =>
                void (async () => {
                    const r = await deleteWorkingPatternAllocation(employee.id, allocationId)
                    if (!r.ok) {
                        showToast(r.error, 'error')
                        return
                    }
                    showToast(wpStrings.allocationDeleted(lang), 'success')
                    if (onWorkingPatternsRefresh) await onWorkingPatternsRefresh()
                })(),
            { confirmLabel: ph.compensation.deleteAction(lang), variant: 'danger' },
        )
    }

    const today = new Date().toISOString().slice(0, 10)
    const workingPatternColumns: PortalDataTableColumn<WorkingPatternHistoryRecord>[] = [
        {
            key: 'name',
            header: ph.compensation.workingPatterns.name(lang),
            render: (r) => r.name || '—',
        },
        {
            key: 'workingSchedule',
            header: ph.compensation.workingPatterns.workingSchedule(lang),
            render: (r) => r.summary ?? '—',
        },
        {
            key: 'fte',
            header: ph.compensation.workingPatterns.fte(lang),
            align: 'right',
            render: (r) =>
                r.fte === null || r.fte === undefined ? '—' : formatFte(Number(r.fte), lang),
        },
        {
            key: 'effectiveDate',
            header: ph.compensation.workingPatterns.effectiveDate(lang),
            render: (r) => formatDisplayDate(r.effective_from),
        },
        {
            key: 'endDate',
            header: ph.compensation.workingPatterns.endDate(lang),
            // "Praegune" marks the row whose validity covers today — NOT the
            // open-ended row: with future allocations queued, the open row may
            // only start months from now (it shows "—" until it takes effect).
            render: (r) => {
                const isCurrent =
                    r.effective_from <= today && (!r.effective_to || r.effective_to >= today)
                const end = r.effective_to ? formatDisplayDate(r.effective_to) : null
                if (!isCurrent) return end ?? '—'
                return (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {end}
                        <span className="portal-timeline__badge portal-timeline__badge--current">
                            {ph.compensation.current(lang)}
                        </span>
                    </span>
                )
            },
        },
        ...(canEditWorkData
            ? [
                  {
                      key: 'actions',
                      header: '',
                      align: 'right',
                      render: (r: WorkingPatternHistoryRecord) =>
                          r.effective_from > today ? (
                              <Button
                                  variant="tertiary"
                                  size="small"
                                  text={ph.compensation.deleteFuture(lang)}
                                  onClick={() => handleDeleteAllocation(r.id)}
                              />
                          ) : null,
                  } satisfies PortalDataTableColumn<WorkingPatternHistoryRecord>,
              ]
            : []),
    ]

    return (
        <>
            <Section>
                <SectionHeader
                    icon={Briefcase}
                    title={ph.profile.employmentWorkloadSection(lang)}
                    actions={
                        <>
                            {/* Dynamic offboarding module: "Lõpeta töösuhe" while the
                                employment is active; "Taasta töösuhe" once ended. Once
                                the leaving details are recorded, the sub-block's own
                                "Muuda" is the entry point for changing them. */}
                            {employee.status === 'active'
                                ? showOffboardAction &&
                                  !hasLeavingData && (
                                      <Button
                                          variant="secondary"
                                          size="small"
                                          text={ph.profile.offboardAction(lang)}
                                          onClick={() => setIsOffboardModalOpen(true)}
                                      />
                                  )
                                : showReactivateAction && (
                                      <Button
                                          variant="secondary"
                                          size="small"
                                          text={ph.profile.reactivateAction(lang)}
                                          isDisabled={statusSaving}
                                          onClick={reactivate}
                                      />
                                  )}
                            {canEdit && !isEditing && (
                                <Button
                                    variant="secondary"
                                    size="small"
                                    text={ph.profile.editProfile(lang)}
                                    onClick={openEdit}
                                />
                            )}
                        </>
                    }
                />

                {isAdmin && (
                    <FieldRow label={ph.profile.employmentStatus(lang)}>
                        {getStatusLabel(employee.status, lang)}
                    </FieldRow>
                )}

                {isEditing ? (
                    <>
                        <FieldRow label={ph.profile.hireDate(lang)}>
                            <TextField
                                inputProps={{ type: 'date' }}
                                value={editHireDate}
                                onChange={(value) => setEditHireDate(value)}
                            />
                        </FieldRow>
                        {showCompensation && (
                            <FieldRow label={ph.compensation.contractStartDate(lang)}>
                                <TextField
                                    inputProps={{ type: 'date' }}
                                    value={editContractStart}
                                    onChange={(value) => setEditContractStart(value)}
                                />
                            </FieldRow>
                        )}
                        <FieldRow label={ph.profile.collaborationType(lang)}>
                            <Select
                                placeholder="—"
                                value={editCollaborationType}
                                onChange={(value) => setEditCollaborationType(value)}
                                options={COLLABORATION_TYPES.map((key) => ({
                                    value: key,
                                    label: ph.profile.collaborationTypes[key](lang),
                                }))}
                            />
                        </FieldRow>
                        <FieldRow label={ph.compensation.probationEnd(lang)}>
                            <TextField
                                inputProps={{ type: 'date' }}
                                value={editProbationEnd}
                                onChange={(value) => setEditProbationEnd(value)}
                            />
                        </FieldRow>
                        <div className="portal-profile__edit-actions">
                            <Button
                                text={ph.profile.saveChanges(lang)}
                                size="small"
                                isDisabled={saving}
                                onClick={() => void submitEdit()}
                            />
                            <Button
                                text={t.common.cancel(lang)}
                                variant="secondary"
                                size="small"
                                onClick={() => setIsEditing(false)}
                            />
                        </div>
                    </>
                ) : (
                    <>
                        <FieldRow label={ph.profile.hireDate(lang)}>
                            {emp?.hire_date ? formatDisplayDate(emp.hire_date) : '—'}
                        </FieldRow>
                        {showCompensation && (
                            <FieldRow label={ph.compensation.contractStartDate(lang)}>
                                {emp?.contract_start ? formatDisplayDate(emp.contract_start) : '—'}
                            </FieldRow>
                        )}
                        {canViewWorkData && (
                            <>
                                <FieldRow label={ph.profile.collaborationType(lang)}>
                                    {isCollaborationType(emp?.collaboration_type)
                                        ? ph.profile.collaborationTypes[emp.collaboration_type](
                                              lang,
                                          )
                                        : '—'}
                                </FieldRow>
                                <FieldRow label={ph.compensation.probationEnd(lang)}>
                                    {emp?.probation_end_date
                                        ? formatDisplayDate(emp.probation_end_date)
                                        : '—'}
                                </FieldRow>
                            </>
                        )}
                    </>
                )}

                {/* Tööaeg & töökoormus (FTE) — SCD2 working-pattern allocations */}
                {canViewWorkData && (
                    <div className="portal-profile__comp-history">
                        <div className="portal-profile__comp-header">
                            <h4 className="portal-profile__subsection-title">
                                {ph.profile.workloadSubsection(lang)}
                            </h4>
                            {canEditWorkData && (
                                <Button
                                    variant="tertiary"
                                    size="small"
                                    iconLeft={Plus}
                                    text={
                                        workingPatterns.some((p) => p.effective_to === null)
                                            ? wpStrings.changePattern(lang)
                                            : wpStrings.assignPattern(lang)
                                    }
                                    onClick={() => setShowAssignPattern(true)}
                                />
                            )}
                        </div>

                        {workingPatterns.length > 0 ? (
                            <PortalDataTable<WorkingPatternHistoryRecord>
                                columns={workingPatternColumns}
                                rows={workingPatterns}
                                rowKey={(r) => r.id}
                            />
                        ) : (
                            <div className="portal-profile__empty-state">
                                {ph.compensation.workingPatterns.noData(lang)}
                            </div>
                        )}
                    </div>
                )}

                {/* Lahkumisandmed: appears once the "Lõpeta töösuhe" form has been
                    confirmed (or legacy leaving fields hold data); admin-only. */}
                {isAdmin && (hasLeavingData || isEditingOffboarding) && (
                    <div className="portal-profile__comp-history">
                        <div className="portal-profile__comp-header">
                            <h4 className="portal-profile__subsection-title">
                                {ph.profile.offboardingDataSection(lang)}
                            </h4>
                            {onUpdatePersonal && !isEditingOffboarding && (
                                <Button
                                    variant="tertiary"
                                    size="small"
                                    text={ph.profile.editProfile(lang)}
                                    onClick={() => {
                                        setIsEditingOffboarding(true)
                                        setEditOffboardingData({
                                            employment_end_date: employee.employment_end_date ?? '',
                                            leaving_file: employee.leaving_file ?? '',
                                            turnover_impact: employee.turnover_impact ?? '',
                                            leaving_reason: employee.leaving_reason ?? '',
                                        })
                                    }}
                                />
                            )}
                        </div>

                        {isEditingOffboarding ? (
                            <>
                                <FieldRow label={ph.profile.employmentEndDate(lang)}>
                                    <TextField
                                        inputProps={{ type: 'date' }}
                                        value={editOffboardingData.employment_end_date ?? ''}
                                        onChange={(value) =>
                                            setEditOffboardingData((prev) => ({
                                                ...prev,
                                                employment_end_date: value,
                                            }))
                                        }
                                    />
                                </FieldRow>
                                <FieldRow label={ph.profile.leavingFile(lang)}>
                                    <TextField
                                        inputProps={{ type: 'url', maxLength: 500 }}
                                        value={editOffboardingData.leaving_file ?? ''}
                                        placeholder="https://..."
                                        onChange={(value) =>
                                            setEditOffboardingData((prev) => ({
                                                ...prev,
                                                leaving_file: value,
                                            }))
                                        }
                                    />
                                </FieldRow>
                                <FieldRow label={ph.profile.turnoverImpact(lang)}>
                                    <Select
                                        placeholder="—"
                                        value={editOffboardingData.turnover_impact ?? ''}
                                        onChange={(value) =>
                                            setEditOffboardingData((prev) => ({
                                                ...prev,
                                                turnover_impact: value,
                                            }))
                                        }
                                        options={TURNOVER_IMPACTS.map((key) => ({
                                            value: key,
                                            label: ph.profile.turnoverImpactLevels[key](lang),
                                        }))}
                                    />
                                </FieldRow>
                                <FieldRow label={ph.profile.leavingReason(lang)}>
                                    <Select
                                        placeholder="—"
                                        value={editOffboardingData.leaving_reason ?? ''}
                                        onChange={(value) =>
                                            setEditOffboardingData((prev) => ({
                                                ...prev,
                                                leaving_reason: value,
                                            }))
                                        }
                                        options={LEAVING_REASONS.map((key) => ({
                                            value: key,
                                            label: ph.profile.leavingReasons[key](lang),
                                        }))}
                                    />
                                </FieldRow>
                                <div className="portal-profile__edit-actions">
                                    <Button
                                        text={ph.profile.saveChanges(lang)}
                                        size="small"
                                        isDisabled={offboardingSaving}
                                        onClick={() => {
                                            if (!onUpdatePersonal) return
                                            setOffboardingSaving(true)
                                            void onUpdatePersonal(editOffboardingData).then(
                                                (ok) => {
                                                    setOffboardingSaving(false)
                                                    if (ok) setIsEditingOffboarding(false)
                                                },
                                            )
                                        }}
                                    />
                                    <Button
                                        text={t.common.cancel(lang)}
                                        variant="secondary"
                                        size="small"
                                        onClick={() => setIsEditingOffboarding(false)}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <FieldRow label={ph.profile.employmentEndDate(lang)}>
                                    {employee.employment_end_date
                                        ? formatDisplayDate(employee.employment_end_date)
                                        : '—'}
                                </FieldRow>
                                <FieldRow label={ph.profile.leavingFile(lang)}>
                                    {employee.leaving_file ? (
                                        <a
                                            href={employee.leaving_file}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="portal-profile__link"
                                        >
                                            {employee.leaving_file}
                                        </a>
                                    ) : (
                                        '—'
                                    )}
                                </FieldRow>
                                <FieldRow label={ph.profile.turnoverImpact(lang)}>
                                    {isTurnoverImpact(employee.turnover_impact)
                                        ? ph.profile.turnoverImpactLevels[employee.turnover_impact](
                                              lang,
                                          )
                                        : (employee.turnover_impact ?? '—')}
                                </FieldRow>
                                <FieldRow label={ph.profile.leavingReason(lang)}>
                                    {isLeavingReason(employee.leaving_reason)
                                        ? ph.profile.leavingReasons[employee.leaving_reason](lang)
                                        : (employee.leaving_reason ?? '—')}
                                </FieldRow>
                            </>
                        )}
                    </div>
                )}
            </Section>

            {isOffboardModalOpen && onOffboard && (
                <PortalOffboardModal
                    isOpen
                    lang={lang}
                    onClose={() => setIsOffboardModalOpen(false)}
                    onSubmit={async (data) => {
                        const ok = await onOffboard(data)
                        if (ok) setIsOffboardModalOpen(false)
                        return ok
                    }}
                />
            )}

            {showAssignPattern ? (
                <>
                    <div
                        className="portal-slide-overlay"
                        onClick={() => setShowAssignPattern(false)}
                    />
                    <div className="portal-slide-over">
                        <div className="portal-slide-over__header">
                            <h3 className="portal-slide-over__title">
                                {wpStrings.assignPattern(lang)}
                            </h3>
                            <Button
                                variant="tertiary"
                                size="small"
                                iconOnly
                                iconLeft={X}
                                text={t.portal.settings.close(lang)}
                                onClick={() => setShowAssignPattern(false)}
                            />
                        </div>
                        <div className="portal-slide-over__body">
                            <div className="portal-slide-over__field">
                                <label>{wpStrings.modeLabel(lang)}</label>
                                <ToggleGroup<'existing' | 'custom'>
                                    ariaLabel={wpStrings.modeLabel(lang)}
                                    value={assignPatternMode}
                                    onChange={(value) => setAssignPatternMode(value)}
                                    options={[
                                        { value: 'existing', label: wpStrings.modeExisting(lang) },
                                        { value: 'custom', label: wpStrings.modeCustom(lang) },
                                    ]}
                                />
                            </div>

                            {assignPatternMode === 'existing' ? (
                                <>
                                    <div className="portal-slide-over__field">
                                        <Select
                                            label={wpStrings.pickPattern(lang)}
                                            value={String(assignPatternId)}
                                            onChange={(value) => setAssignPatternId(Number(value))}
                                            options={availablePatterns.map((p) => ({
                                                value: String(p.id),
                                                label: `${p.name}${p.summary ? ` — ${p.summary}` : ''}`,
                                            }))}
                                        />
                                    </div>
                                    {existingPreview && (
                                        <div className="portal-slide-over__field">
                                            <label>{wpStrings.weeklySchedule(lang)}</label>
                                            <WorkingPatternDayGrid
                                                mode={existingPreview.mode}
                                                days={existingPreview.days}
                                                onChange={() => {}}
                                                dayLabels={wpDayLabels}
                                                readOnly
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <div className="portal-slide-over__field">
                                        <TextField
                                            label={wpStrings.name(lang)}
                                            value={assignName}
                                            onChange={(value) => setAssignName(value)}
                                        />
                                    </div>
                                    <div
                                        className="portal-slide-over__field"
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 1fr',
                                            gap: 12,
                                            // One label wraps to two lines — keep the
                                            // input boxes on a shared baseline.
                                            alignItems: 'end',
                                        }}
                                    >
                                        <TextField
                                            label={wpStrings.fullTimeHours(lang)}
                                            inputProps={{
                                                type: 'number',
                                                min: 1,
                                                max: 80,
                                                step: 0.5,
                                            }}
                                            value={assignFullTimeHours}
                                            onChange={(value) => setAssignFullTimeHours(value)}
                                        />
                                        <TextField
                                            label={wpStrings.standardDayHours(lang)}
                                            inputProps={{
                                                type: 'number',
                                                min: 1,
                                                max: 24,
                                                step: 0.5,
                                            }}
                                            value={assignStandardDay}
                                            onChange={(value) => setAssignStandardDay(value)}
                                        />
                                    </div>
                                    <div className="portal-slide-over__field">
                                        <ToggleGroup<'fixed' | 'flex'>
                                            ariaLabel={wpStrings.modeLabel(lang)}
                                            value={assignCustomKind}
                                            onChange={(value) => setAssignCustomKind(value)}
                                            options={[
                                                {
                                                    value: 'fixed',
                                                    label: wpStrings.modeFixed(lang),
                                                },
                                                { value: 'flex', label: wpStrings.modeFlex(lang) },
                                            ]}
                                        />
                                    </div>
                                    {assignCustomKind === 'flex' && (
                                        <div className="portal-slide-over__field">
                                            <TextField
                                                label={wpStrings.weeklyHours(lang)}
                                                inputProps={{
                                                    type: 'number',
                                                    min: 0.5,
                                                    max: 80,
                                                    step: 0.5,
                                                }}
                                                value={assignWeeklyHours}
                                                onChange={(value) => setAssignWeeklyHours(value)}
                                            />
                                        </div>
                                    )}
                                    <div className="portal-slide-over__field">
                                        <label>{wpStrings.weeklySchedule(lang)}</label>
                                        <WorkingPatternDayGrid
                                            mode={assignCustomKind}
                                            days={assignDays}
                                            onChange={(days) => setAssignDays(days)}
                                            dayLabels={wpDayLabels}
                                            standardDayHours={assignStandardDayHours}
                                        />
                                    </div>
                                    <WorkingPatternFtePreview
                                        workingHours={assignWorkingHours}
                                        fullTimeHours={assignFullTime}
                                        daysPerWeek={assignDaysPerWeek}
                                        lang={lang}
                                    />
                                </>
                            )}

                            <div className="portal-slide-over__field">
                                <TextField
                                    label={wpStrings.effectiveFrom(lang)}
                                    inputProps={{
                                        type: 'date',
                                        min: new Date().toISOString().slice(0, 10),
                                    }}
                                    value={assignEffectiveFrom}
                                    onChange={(value) => setAssignEffectiveFrom(value)}
                                    note={{ text: wpStrings.effectiveFromHint(lang) }}
                                />
                            </div>
                        </div>
                        <div className="portal-slide-over__footer">
                            <Button
                                variant="tertiary"
                                size="small"
                                text={t.portal.settings.close(lang)}
                                onClick={() => setShowAssignPattern(false)}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                text={
                                    assignSubmitting ? wpStrings.saving(lang) : wpStrings.save(lang)
                                }
                                isDisabled={assignSubmitting || !assignReady}
                                onClick={() => void handleAssignPattern()}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </>
    )
}
